import { HTTP } from "@/core/http";
import { BusinessRequirementsService } from "../../business-requirements/services/businessRequirementsService";
import type {
  BusinessRequirementApiRow,
  BusinessRequirementHistory,
  BusinessRequirementMetadataOptionItem,
} from "../../business-requirements/types";
import type {
  BusinessRequirement,
  BusinessRequirementEstimatesGenerationApiResponse,
  BusinessRequirementEstimatesMetadata,
  BusinessRequirementEstimationParams,
  GenerateBusinessRequirementEstimatesResult,
  UpdateBusinessRequirementEstimateInput,
} from "../types";

const BASE = "/pms/business-requirement-estimates";

type ApiRow = Partial<BusinessRequirementApiRow> & Record<string, unknown>;

function toQueryParams(params: BusinessRequirementEstimationParams) {
  const moduleName = params.moduleName.trim();
  if (!moduleName) {
    throw new Error("Select a module to estimate.");
  }
  return {
    projectId: params.projectId,
    moduleName,
  };
}

function readCount(
  raw: Partial<BusinessRequirementEstimatesGenerationApiResponse> | undefined,
  camel: "estimatedCount" | "skippedCount",
  snake: "estimated_count" | "skipped_count",
): number | undefined {
  const data = raw?.data;
  const value = raw?.[camel] ?? data?.[camel] ?? data?.[snake];
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

function optionList(
  raw: Record<string, unknown>,
  key: string,
): BusinessRequirementMetadataOptionItem[] {
  const value = raw[key];
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    if (item == null || typeof item !== "object") return [];
    const option = item as { enum?: unknown; label?: unknown };
    if (typeof option.enum !== "string") return [];
    const label =
      typeof option.label === "string" && option.label.length > 0
        ? option.label
        : option.enum;
    return [{ enum: option.enum, label }];
  });
}

/**
 * HTTP client for Business Requirement Estimates
 * (`/pms/business-requirement-estimates/...`).
 */
export class BusinessRequirementEstimatesService {
  private readonly businessRequirements: BusinessRequirementsService;

  constructor(private readonly httpClient: HTTP) {
    this.businessRequirements = new BusinessRequirementsService(httpClient);
  }

  private fromApi(raw: ApiRow): BusinessRequirement {
    return this.businessRequirements.fromApiBusinessRequirement(raw);
  }

  /** GET /project/{projectId}. PMS already returns only Open and Approved rows. */
  async listByProject(
    projectId: number,
    module?: string,
  ): Promise<BusinessRequirement[]> {
    const params: Record<string, string> = {};
    const moduleName = module?.trim();
    if (moduleName) params.module = moduleName;
    const raw = await this.httpClient.get<ApiRow[]>(
      `${BASE}/project/${projectId}`,
      { params },
    );
    return (raw ?? []).map((row) => this.fromApi(row));
  }

  async getMetadata(): Promise<BusinessRequirementEstimatesMetadata> {
    const raw = await this.httpClient.get<Record<string, unknown>>(
      `${BASE}/metadata`,
    );
    return {
      estimationStatus: optionList(raw ?? {}, "estimationStatus"),
      complexity: optionList(raw ?? {}, "complexity"),
      status: optionList(raw ?? {}, "status"),
    };
  }

  /** PUT /{busReqId} — final effort, rationale, and remarks. */
  async updateEstimate(
    input: UpdateBusinessRequirementEstimateInput,
  ): Promise<BusinessRequirement> {
    const raw = await this.httpClient.put<ApiRow>(`${BASE}/${input.busReqId}`, {
      body: {
        busReqFinalEffort: input.busReqFinalEffort,
        busReqEffortRationale: input.busReqEffortRationale,
        busReqRemarks: input.busReqRemarks,
      },
    });
    return this.fromApi(raw);
  }

  async getHistory(busReqId: number): Promise<BusinessRequirementHistory[]> {
    return this.httpClient.get<BusinessRequirementHistory[]>(
      `${BASE}/${busReqId}/history`,
    );
  }

  /**
   * POST /generate?projectId=&moduleName=.
   * PMS resolves technology stack and domain from RFP Analysis and estimates the
   * module's unestimated Open and Approved requirements. The UI does not send
   * the requirement list.
   */
  async generateEstimates(
    params: BusinessRequirementEstimationParams,
  ): Promise<GenerateBusinessRequirementEstimatesResult> {
    const raw = await this.httpClient.post<
      Partial<BusinessRequirementEstimatesGenerationApiResponse>
    >(`${BASE}/generate`, {
      params: toQueryParams(params),
    });
    const estimated = readCount(raw, "estimatedCount", "estimated_count");
    if (estimated == null) {
      throw new Error("Estimation did not complete. Please try again.");
    }
    return {
      estimated,
      skipped: readCount(raw, "skippedCount", "skipped_count") ?? 0,
    };
  }
}
